import { otto } from "../../../exports/nexus.js";
import type {
  NavigationScope,
  NavigationScopeName,
  NavigationScopeOptions,
} from "./scope.types.js";
import { NAV_SCOPES } from "../lib/utils.js";
import { getCurrentNavigation } from "../lib/navigation.client.js";
import { defineContext, historyPatched } from "../lib/history.client.js";
import * as cache from "./cache.js";
import {
  ensureEntry,
  ensureRegExpEntry,
  getScopedState,
  initializeNamespaceWithCurrentNavigation,
} from "./utils.js";
import { scopeScope as log } from "./scope.logger.js";
import { getScopeNavigate } from "./navigate.js";

export const scope = <N extends NavigationScopeName, O extends NavigationScopeOptions>(
  name: N,
  options: O = {} as O,
): NavigationScope<N, O> => {
  /*                                                                                       */
  const { hash: hashFilters = [], search: searchFilters = [] } = options;
  const pathPattern = options.path ? new RegExp(options.path) : undefined;

  const scopeId = `${name}:${hashFilters.join("|")}:${searchFilters.join("|")}:${pathPattern?.toString()}`;
  log.info("Aquiring scope", scopeId, "with options", options);

  /*                                              */
  if (cache.scopeCache.has(scopeId)) return cache.scopeCache.get(scopeId) as NavigationScope<N, O>;

  /*                                              */
  const namespace = otto.navigation[NAV_SCOPES][scopeId];

  /*                                                         */
  const [, state, hash, search] = getCurrentNavigation(
    undefined,
    hashFilters,
    searchFilters,
    options,
  );

  const scopeState = getScopedState(name, state);
  initializeNamespaceWithCurrentNavigation(
    namespace,
    scopeState.clone(),
    hash.clone(),
    search.clone(),
  );

  /*                     */
  const res: NavigationScope<string, NavigationScopeOptions> = {
    name,
    key: scopeId,
    current: namespace.current,
    currentHash: namespace.currentHash,
    currentSearch: namespace.currentSearch,
    currentState: namespace.currentState,
    onNavigate: namespace.onNavigate,

    hash(params) {
      if (params) hash.fromObject(params);
      return hash as never;
    },

    search(params) {
      if (params) search.fromObject(params);
      return search as never;
    },

    state(params) {
      if (params) scopeState.fromObject(params as never);
      return scopeState as never;
    },

    reset() {
      throw new Error("Reset is not implemented yet");
    },

    navigate: getScopeNavigate(name, search, hash, scopeState),

    push: (o, url) => res.navigate(url, { ...o, history: "push" }),
    replace: (o, url) => res.navigate(url, { ...o, history: "replace" }),

    back: async (o) =>
      (await historyPatched()).back.call(defineContext({ ...o, scope: name })) ?? true,
    forward: async (o) =>
      (await historyPatched()).forward.call(defineContext({ ...o, scope: name })) ?? true,

    reload: () => location.reload() ?? true,
    go: async (delta, o) =>
      (await historyPatched()).go.call(defineContext({ ...o, scope: name }), delta) ?? true,
  };

  /*                                           */
  cache.scopeCache.set(scopeId, res);

  /*                                      */
  ensureEntry(cache.scopesFiltered, name).add(res);
  ensureRegExpEntry(cache.pathFiltered, pathPattern)?.add(res);

  hashFilters.forEach((ho) => ensureEntry(cache.hashFiltered, ho).add(res));
  searchFilters.forEach((so) => ensureEntry(cache.searchFiltered, so).add(res));

  return res as NavigationScope<N, O>;
};
